import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import { CreateTaskItemDto, FetchError, queryKeys, useAccessToken, UserDto } from "src/http";

type Variables = {
  taskItemGuid: string;
  teamSlug?: string;
  description: string;
  parentTaskItemGuid?: string;
  contextActionItemGuid?: string;
  parentIssueGuid?: string;
  note: string;
  dueDate: string | null;
  owners: UserDto[];
  primaryOwner: UserDto | null;
  createdFromSurveyResponseAnswerGuid?: string;
};

const useCreateTaskItemRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function createTaskItemRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: CreateTaskItemDto = {
      guid: variables.taskItemGuid,
      teamSlug: variables.teamSlug,
      description: variables.description,
      parentTaskItemGuid: variables.parentTaskItemGuid,
      contextActionItemGuid: variables.contextActionItemGuid,
      parentIssueGuid: variables.parentIssueGuid,
      note: variables.note,
      dueDate: variables.dueDate,
      ownerUserIds: variables.owners?.map((x) => x.userId),
      primaryOwnerUserId: variables.primaryOwner?.userId,
      createdFromSurveyResponseAnswerGuid: variables.createdFromSurveyResponseAnswerGuid,
    };

    const response = await fetch(`${configuration.apiRootUrl}/task-items`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response;
  }

  return { createTaskItemRequest };
};

const useCreateTaskItem = () => {
  const { createTaskItemRequest } = useCreateTaskItemRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: createTaskItemRequest,
    onSuccess: (_data, variables) => {
      if (variables.parentTaskItemGuid) {
        signalR.sendRefetchTaskItem(variables.parentTaskItemGuid);
      }

      if (variables.contextActionItemGuid) {
        signalR.sendRefetchActionItem(variables.contextActionItemGuid);
      }

      if (variables.parentIssueGuid) {
        signalR.sendRefetchIssue(variables.parentIssueGuid);
      }

      signalR.sendRefetchTeamTaskItems();
      signalR.sendRefetchUserTaskItems();
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItems);
      queryClient.invalidateQueries(queryKeys.taskItems.allTeamTaskItemSubtasks);
      queryClient.invalidateQueries(queryKeys.taskItems.allUserTaskItems);

      if (variables.parentTaskItemGuid) {
        queryClient.invalidateQueries(queryKeys.taskItems.getTaskItemSubtasks(variables.parentTaskItemGuid));
      }

      // Update supporting items list
      if (variables.contextActionItemGuid) {
        queryClient.invalidateQueries(queryKeys.actionItems.item(variables.contextActionItemGuid));
      }

      if (variables.parentIssueGuid) {
        queryClient.invalidateQueries(queryKeys.taskItems.getIssueTaskItems(variables.parentIssueGuid));
      }

      if (variables.createdFromSurveyResponseAnswerGuid) {
        queryClient.invalidateQueries(
          queryKeys.surveys.getItemsCreatedFromResponseAnswer(variables.createdFromSurveyResponseAnswerGuid),
        );
      }
    },
  });
};

export { useCreateTaskItem };
